'use client';
import React, { useState } from 'react';
import { DollarSign, Users, CheckCircle, Clock, Send, Download, Calendar, Zap, ChevronDown, ChevronUp, FileText, RefreshCw, Calculator, Check, X } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';


// Backend integration: fetch from /api/v1/finance/distributions/summary
const distMetrics = {
  pendingApproval: 3,
  totalPendingValue: '$2,847,440',
  scheduledThisMonth: 6,
  dispatchedMTD: '$1,224,000',
  investorsCovered: 473,
  avgProRataAccuracy: '99.97%',
};

// Backend integration: fetch from /api/v1/finance/distributions/batches
const distributionBatches = [
  {
    id: 'db-001',
    batchRef: 'DIST-2026-0841',
    campaign: 'Thornfield RE Fund II',
    distributionType: 'Quarterly Income',
    totalAmount: '$287,440',
    investorCount: 142,
    dueDate: '2026-08-25',
    disbursementMode: 'scheduled',
    status: 'pending-approval',
    submittedBy: 'J. Harrington',
    submittedAt: '2026-08-20 09:15',
    proRataMethod: 'Proportional to investment value',
    fundingSource: 'Escrow — Thornfield RE Fund II',
    approvedBy: null,
    notes: 'Q2 2026 income distribution per fund agreement clause 8.3',
    proRataBreakdown: [
      { investor: 'Ashbury Pension Trust', investment: '$450,000', share: '15.65%', amount: '$44,987', accountRef: 'IBAN-GB29NWBK60161331926819' },
      { investor: 'Crestwood Family Office LLC', investment: '$380,000', share: '13.22%', amount: '$37,979', accountRef: 'IBAN-GB29NWBK60161331926820' },
      { investor: 'Meridian Pension Trust APAC', investment: '$320,000', share: '11.13%', amount: '$31,982', accountRef: 'IBAN-GB29NWBK60161331926821' },
      { investor: 'Priya Narasimhan', investment: '$75,000', share: '2.61%', amount: '$7,502', accountRef: 'IBAN-GB29NWBK60161331926822' },
      { investor: '138 other investors', investment: '$1,650,000', share: '57.39%', amount: '$164,990', accountRef: 'Multiple' },
    ],
  },
  {
    id: 'db-002',
    batchRef: 'DIST-2026-0839',
    campaign: 'Meridian Logistics Hub — Series A',
    distributionType: 'Capital Return',
    totalAmount: '$1,842,000',
    investorCount: 88,
    dueDate: '2026-09-01',
    disbursementMode: 'scheduled',
    status: 'scheduled',
    submittedBy: 'System',
    submittedAt: '2026-08-19 14:00',
    proRataMethod: 'Proportional to units held',
    fundingSource: 'Escrow — Meridian LH Series A',
    approvedBy: 'K. Osei',
    notes: 'Partial capital return following Series A close. Pro-rata per unit register.',
    proRataBreakdown: [
      { investor: 'Hargreaves Investment Co.', investment: '$600,000', share: '32.57%', amount: '$599,999', accountRef: 'IBAN-GB29NWBK60161331926830' },
      { investor: 'Jan Kowalski', investment: '$250,000', share: '13.57%', amount: '$249,999', accountRef: 'IBAN-GB29NWBK60161331926831' },
      { investor: '86 other investors', investment: '$990,000', share: '53.86%', amount: '$992,002', accountRef: 'Multiple' },
    ],
  },
  {
    id: 'db-003',
    batchRef: 'DIST-2026-0835',
    campaign: 'Ashford Commercial REIT I',
    distributionType: 'Quarterly Income',
    totalAmount: '$412,000',
    investorCount: 87,
    dueDate: '2026-09-15',
    disbursementMode: 'immediate',
    status: 'pending-approval',
    submittedBy: 'J. Harrington',
    submittedAt: '2026-08-20 11:30',
    proRataMethod: 'Proportional to investment value',
    fundingSource: 'Escrow — Ashford REIT I',
    approvedBy: null,
    notes: 'Q2 2026 REIT income. Immediate disbursement requested by fund manager.',
    proRataBreakdown: [
      { investor: 'Thornfield Capital Holdings Ltd', investment: '$900,000', share: '21.84%', amount: '$89,980', accountRef: 'IBAN-GB29NWBK60161331926840' },
      { investor: 'Crestwood Family Office LLC', investment: '$750,000', share: '18.20%', amount: '$74,984', accountRef: 'IBAN-GB29NWBK60161331926841' },
      { investor: '85 other investors', investment: '$2,470,000', share: '59.96%', amount: '$247,036', accountRef: 'Multiple' },
    ],
  },
  {
    id: 'db-004',
    batchRef: 'DIST-2026-0820',
    campaign: 'ClearPath Clean Energy Fund II',
    distributionType: 'Annual Distribution',
    totalAmount: '$624,800',
    investorCount: 156,
    dueDate: '2026-10-01',
    disbursementMode: 'scheduled',
    status: 'scheduled',
    submittedBy: 'System',
    submittedAt: '2026-08-15 08:00',
    proRataMethod: 'Proportional to investment value',
    fundingSource: 'Escrow — ClearPath CEF II',
    approvedBy: 'K. Osei',
    notes: 'Annual distribution per fund prospectus. Scheduled for Oct 1 settlement.',
    proRataBreakdown: [
      { investor: 'Meridian Pension Trust APAC', investment: '$1,200,000', share: '19.20%', amount: '$119,962', accountRef: 'IBAN-GB29NWBK60161331926850' },
      { investor: '155 other investors', investment: '$5,050,000', share: '80.80%', amount: '$504,838', accountRef: 'Multiple' },
    ],
  },
  {
    id: 'db-005',
    batchRef: 'DIST-2026-0812',
    campaign: 'Vantage Private Credit Fund I',
    distributionType: 'Interest Payment',
    totalAmount: '$181,200',
    investorCount: 34,
    dueDate: '2026-08-18',
    disbursementMode: 'immediate',
    status: 'dispatched',
    submittedBy: 'J. Harrington',
    submittedAt: '2026-08-17 10:00',
    proRataMethod: 'Proportional to loan participation',
    fundingSource: 'Escrow — Vantage PCF I',
    approvedBy: 'K. Osei',
    notes: 'Monthly interest payment. Dispatched via ACH batch.',
    proRataBreakdown: [],
  },
];

const statusConfig: Record<string, { label: string; variant: 'pending' | 'active' | 'confirmed' | 'settled' | 'warning'; color: string }> = {
  'pending-approval': { label: 'Pending Approval', variant: 'pending', color: 'text-warning' },
  'scheduled': { label: 'Scheduled', variant: 'active', color: 'text-info' },
  'dispatched': { label: 'Dispatched', variant: 'confirmed', color: 'text-positive' },
  'failed': { label: 'Failed', variant: 'warning', color: 'text-negative' },
};

const modeConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  immediate: { icon: Zap, label: 'Immediate', color: 'text-warning' },
  scheduled: { icon: Calendar, label: 'Scheduled', color: 'text-info' },
};

interface ReceiptModalProps {
  batch: typeof distributionBatches[0];
  onClose: () => void;
}

function ReceiptModal({ batch, onClose }: ReceiptModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Distribution Receipt</p>
            <h2 className="text-lg font-bold text-foreground">{batch.batchRef}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-3 text-muted-foreground hover:text-foreground transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Receipt header */}
          <div className="bg-surface-3 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Campaign</p>
              <p className="font-semibold text-foreground">{batch.campaign}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Distribution Type</p>
              <p className="font-semibold text-foreground">{batch.distributionType}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Amount</p>
              <p className="font-bold text-foreground font-mono-data text-base">{batch.totalAmount}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Investors</p>
              <p className="font-semibold text-foreground">{batch.investorCount}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Disbursement Mode</p>
              <p className="font-semibold text-foreground capitalize">{batch.disbursementMode}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Due Date</p>
              <p className="font-semibold text-foreground">{batch.dueDate}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Funding Source</p>
              <p className="font-semibold text-foreground">{batch.fundingSource}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Pro-Rata Method</p>
              <p className="font-semibold text-foreground">{batch.proRataMethod}</p>
            </div>
          </div>

          {/* Pro-rata breakdown */}
          {batch.proRataBreakdown.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Pro-Rata Breakdown</p>
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-3 border-b border-border">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Investor</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Investment</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Share</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batch.proRataBreakdown.map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-3/50 transition-colors">
                        <td className="px-4 py-2.5 text-foreground font-medium">{row.investor}</td>
                        <td className="px-4 py-2.5 text-right font-mono-data text-muted-foreground">{row.investment}</td>
                        <td className="px-4 py-2.5 text-right font-mono-data text-info">{row.share}</td>
                        <td className="px-4 py-2.5 text-right font-mono-data font-semibold text-foreground">{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-surface-3 border-t border-border">
                      <td colSpan={3} className="px-4 py-2.5 text-xs font-semibold text-muted-foreground">Total</td>
                      <td className="px-4 py-2.5 text-right font-mono-data font-bold text-foreground">{batch.totalAmount}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {batch.notes && (
            <div className="bg-surface-3 rounded-xl p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-foreground">{batch.notes}</p>
            </div>
          )}

          {/* Approval info */}
          <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-4">
            <span>Submitted by {batch.submittedBy} · {batch.submittedAt}</span>
            {batch.approvedBy && <span className="text-positive font-medium">✓ Approved by {batch.approvedBy}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 p-6 border-t border-border">
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors">
            <Download size={14} />
            Download PDF Receipt
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-surface-3 text-foreground rounded-lg text-sm font-semibold hover:bg-surface-3/80 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface ApproveModalProps {
  batch: typeof distributionBatches[0];
  onClose: () => void;
  onApprove: (id: string, mode: string) => void;
}

function ApproveModal({ batch, onClose, onApprove }: ApproveModalProps) {
  const [mode, setMode] = useState(batch.disbursementMode);
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 border border-border rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Approve Distribution</p>
            <h2 className="text-lg font-bold text-foreground">{batch.batchRef}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-3 text-muted-foreground hover:text-foreground transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="bg-surface-3 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Campaign</span>
              <span className="font-semibold text-foreground">{batch.campaign}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Amount</span>
              <span className="font-bold font-mono-data text-foreground">{batch.totalAmount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Investors</span>
              <span className="font-semibold text-foreground">{batch.investorCount}</span>
            </div>
          </div>

          {/* Disbursement mode selector */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Disbursement Mode</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode('immediate')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${mode === 'immediate' ? 'border-warning bg-warning/10' : 'border-border bg-surface-3 hover:border-border/80'}`}
              >
                <Zap size={20} className={mode === 'immediate' ? 'text-warning' : 'text-muted-foreground'} />
                <span className={`text-sm font-semibold ${mode === 'immediate' ? 'text-warning' : 'text-muted-foreground'}`}>Immediate</span>
                <span className="text-xs text-muted-foreground text-center">Dispatch within 2 hours of approval</span>
              </button>
              <button
                onClick={() => setMode('scheduled')}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${mode === 'scheduled' ? 'border-info bg-info/10' : 'border-border bg-surface-3 hover:border-border/80'}`}
              >
                <Calendar size={20} className={mode === 'scheduled' ? 'text-info' : 'text-muted-foreground'} />
                <span className={`text-sm font-semibold ${mode === 'scheduled' ? 'text-info' : 'text-muted-foreground'}`}>Scheduled</span>
                <span className="text-xs text-muted-foreground text-center">Dispatch on due date: {batch.dueDate}</span>
              </button>
            </div>
          </div>

          {/* Confirmation checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              onClick={() => setConfirmed(!confirmed)}
              className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${confirmed ? 'bg-primary border-primary' : 'border-border bg-surface-3'}`}
            >
              {confirmed && <Check size={12} className="text-primary-foreground" />}
            </div>
            <span className="text-sm text-muted-foreground">
              I confirm that the pro-rata calculations have been reviewed and the distribution is authorised per fund agreement.
            </span>
          </label>
        </div>
        <div className="flex items-center gap-3 p-6 border-t border-border">
          <button
            disabled={!confirmed}
            onClick={() => { onApprove(batch.id, mode); onClose(); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-positive text-white rounded-lg text-sm font-semibold hover:bg-positive/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle size={15} />
            Approve & {mode === 'immediate' ? 'Dispatch Now' : 'Schedule'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 bg-surface-3 text-foreground rounded-lg text-sm font-semibold hover:bg-surface-3/80 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DistributionManagement() {
  const [activeTab, setActiveTab] = useState<'queue' | 'scheduled' | 'dispatched'>('queue');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [receiptBatch, setReceiptBatch] = useState<typeof distributionBatches[0] | null>(null);
  const [approveBatch, setApproveBatch] = useState<typeof distributionBatches[0] | null>(null);
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());

  const handleApprove = (id: string, mode: string) => {
    setApprovedIds(prev => new Set([...prev, id]));
  };

  const tabBatches = {
    queue: distributionBatches.filter(b => b.status === 'pending-approval'),
    scheduled: distributionBatches.filter(b => b.status === 'scheduled'),
    dispatched: distributionBatches.filter(b => b.status === 'dispatched'),
  };

  const currentBatches = tabBatches[activeTab];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Finance Portal</p>
          <h1 className="text-2xl font-bold text-foreground">Distribution Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Approve and dispatch investor distributions with pro-rata calculations</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-3 py-2 bg-surface-3 text-foreground rounded-lg text-sm font-medium hover:bg-surface-3/80 transition-colors border border-border">
            <RefreshCw size={14} />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-surface-3 text-foreground rounded-lg text-sm font-medium hover:bg-surface-3/80 transition-colors border border-border">
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard label="Pending Approval" value={String(distMetrics.pendingApproval)} variant="warning" icon={Clock} />
        <MetricCard label="Pending Value" value={distMetrics.totalPendingValue} variant="default" icon={DollarSign} mono />
        <MetricCard label="Scheduled (Month)" value={String(distMetrics.scheduledThisMonth)} variant="info" icon={Calendar} />
        <MetricCard label="Dispatched MTD" value={distMetrics.dispatchedMTD} variant="positive" icon={Send} mono />
        <MetricCard label="Investors Covered" value={String(distMetrics.investorsCovered)} variant="default" icon={Users} />
        <MetricCard label="Pro-Rata Accuracy" value={distMetrics.avgProRataAccuracy} variant="positive" icon={Calculator} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-surface-3 rounded-xl p-1 w-fit">
        {([
          { key: 'queue', label: 'Approval Queue', count: tabBatches.queue.length },
          { key: 'scheduled', label: 'Scheduled', count: tabBatches.scheduled.length },
          { key: 'dispatched', label: 'Dispatched', count: tabBatches.dispatched.length },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.key ? 'bg-surface-2 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs font-mono-data px-1.5 py-0.5 rounded-full ${activeTab === tab.key && tab.key === 'queue' ? 'bg-warning/20 text-warning' : 'bg-surface-2 text-muted-foreground'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Distribution batch list */}
      <div className="space-y-3">
        {currentBatches.length === 0 && (
          <div className="glass-card rounded-xl p-10 text-center text-muted-foreground text-sm">
            No distributions in this category.
          </div>
        )}
        {currentBatches.map(batch => {
          const isApproved = approvedIds.has(batch.id);
          const isExpanded = expandedId === batch.id;
          const ModeIcon = modeConfig[batch.disbursementMode].icon;

          return (
            <div key={batch.id} className="glass-card rounded-xl overflow-hidden">
              {/* Batch header row */}
              <div className="flex items-center gap-4 p-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <span className="font-mono-data text-xs text-muted-foreground">{batch.batchRef}</span>
                    <StatusBadge
                      variant={isApproved ? 'confirmed' : statusConfig[batch.status]?.variant ?? 'pending'}
                      label={isApproved ? 'Approved' : statusConfig[batch.status]?.label ?? batch.status}
                      dot
                    />
                    <span className={`flex items-center gap-1 text-xs font-semibold ${modeConfig[batch.disbursementMode].color}`}>
                      <ModeIcon size={12} />
                      {modeConfig[batch.disbursementMode].label}
                    </span>
                  </div>
                  <p className="font-semibold text-foreground text-sm">{batch.campaign}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{batch.distributionType} · {batch.investorCount} investors · Due {batch.dueDate}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold font-mono-data text-foreground text-lg">{batch.totalAmount}</p>
                  <p className="text-xs text-muted-foreground">Total distribution</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(batch.status === 'pending-approval' && !isApproved) && (
                    <button
                      onClick={() => setApproveBatch(batch)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-positive/10 text-positive border border-positive/30 rounded-lg text-xs font-semibold hover:bg-positive/20 transition-colors"
                    >
                      <CheckCircle size={13} />
                      Approve
                    </button>
                  )}
                  <button
                    onClick={() => setReceiptBatch(batch)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-3 text-muted-foreground border border-border rounded-lg text-xs font-semibold hover:text-foreground transition-colors"
                  >
                    <FileText size={13} />
                    Receipt
                  </button>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : batch.id)}
                    className="p-1.5 rounded-lg bg-surface-3 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </button>
                </div>
              </div>

              {/* Expanded pro-rata breakdown */}
              {isExpanded && (
                <div className="border-t border-border bg-surface-3/40 p-5 space-y-4">
                  {/* Pro-rata method */}
                  <div className="flex items-center gap-3">
                    <Calculator size={14} className="text-primary flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Pro-Rata Method</p>
                      <p className="text-sm text-foreground">{batch.proRataMethod}</p>
                    </div>
                    <div className="ml-auto">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Funding Source</p>
                      <p className="text-sm text-foreground">{batch.fundingSource}</p>
                    </div>
                  </div>

                  {/* Pro-rata table */}
                  {batch.proRataBreakdown.length > 0 && (
                    <div className="rounded-xl border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-surface-2 border-b border-border">
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Investor</th>
                            <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Investment</th>
                            <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Share %</th>
                            <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Distribution</th>
                            <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Account Ref</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batch.proRataBreakdown.map((row, i) => (
                            <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-3/50 transition-colors">
                              <td className="px-4 py-2.5 font-medium text-foreground">{row.investor}</td>
                              <td className="px-4 py-2.5 text-right font-mono-data text-muted-foreground">{row.investment}</td>
                              <td className="px-4 py-2.5 text-right font-mono-data text-info">{row.share}</td>
                              <td className="px-4 py-2.5 text-right font-mono-data font-semibold text-foreground">{row.amount}</td>
                              <td className="px-4 py-2.5 text-right font-mono-data text-xs text-muted-foreground">{row.accountRef}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-surface-2 border-t border-border">
                            <td colSpan={3} className="px-4 py-2.5 text-xs font-semibold text-muted-foreground">Total</td>
                            <td className="px-4 py-2.5 text-right font-mono-data font-bold text-foreground">{batch.totalAmount}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* Notes + submitted by */}
                  {batch.notes && (
                    <div className="bg-surface-2 rounded-lg p-3 text-sm text-muted-foreground border border-border">
                      <span className="font-semibold text-foreground">Notes: </span>{batch.notes}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Submitted by <span className="text-foreground font-medium">{batch.submittedBy}</span> · {batch.submittedAt}</span>
                    {batch.approvedBy && (
                      <span className="flex items-center gap-1 text-positive font-medium">
                        <CheckCircle size={12} /> Approved by {batch.approvedBy}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modals */}
      {receiptBatch && <ReceiptModal batch={receiptBatch} onClose={() => setReceiptBatch(null)} />}
      {approveBatch && <ApproveModal batch={approveBatch} onClose={() => setApproveBatch(null)} onApprove={handleApprove} />}
    </div>
  );
}
